import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  updateDoc,
  doc,
  getDocs,
  writeBatch,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../../config/firebase";
import { useEffect } from "react";
import {
  INotification,
  NotificationPreferences,
} from "@freetech/models/notifications";
import { INotification as INotificationV2 } from "@freetech/models/notifications";
import { apiClient } from "../../api/apiClient";

export const useNotifications = (userId: string) => {
  const queryClient = useQueryClient();

  const { data: notifications = [] } = useQuery({
    queryKey: ["notifications", userId],
    queryFn: async () => {
      const q = query(
        collection(db, "notifications"),
        where("recipientId", "==", userId),
        orderBy("createdAt", "desc"),
        limit(10)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt.toLocaleString(),
      })) as INotificationV2[];
    },
    staleTime: Infinity,
    enabled: !!userId,
  });

  const {
    data: notificationPreferences,
    refetch: refetchNotificationPreferences,
  } = useQuery({
    queryKey: ["notificationPreferences", userId],
    queryFn: async () => {
      if (!userId) {
        throw new Error(
          "User ID is required to fetch notification preferences"
        );
      }
      return apiClient.shared.getNotificationPreferences(userId);
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!userId,
  });

  useEffect(() => {
    if (!userId) return;

    const q = query(
      collection(db, "notifications"),
      where("recipientId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(10)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "added" || change.type === "modified") {
          queryClient.setQueryData(
            ["notifications", userId],
            (oldData: INotificationV2[] = []) => {
              const newNotification = {
                id: change.doc.id,
                ...change.doc.data(),
                createdAt: change.doc.data().createdAt,
              } as INotificationV2;

              const filteredOldData = oldData.filter(
                (n) => n.id !== newNotification.id
              );
              return [newNotification, ...filteredOldData].slice(0, 10);
            }
          );
        }

        if (change.type === "removed") {
          queryClient.setQueryData(
            ["notifications", userId],
            (oldData: INotificationV2[] = []) =>
              oldData.filter((n) => n.id !== change.doc.id)
          );
        }
      });
    });

    return () => unsubscribe();
  }, [userId, queryClient]);

  const markAsRead = useMutation({
    mutationFn: async (notificationId: string) => {
      const notificationRef = doc(db, "notifications", notificationId);
      await updateDoc(notificationRef, { read: true });
    },
    onMutate: async (notificationId: string) => {
      await queryClient.cancelQueries({ queryKey: ["notifications", userId] });

      const previousNotifications = queryClient.getQueryData([
        "notifications",
        userId,
      ]);

      queryClient.setQueryData(
        ["notifications", userId],
        (old: INotificationV2[] = []) =>
          old.map((notification) =>
            notification.id === notificationId
              ? { ...notification, read: true }
              : notification
          )
      );

      return { previousNotifications };
    },
    onError: (err, variables, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          ["notifications", userId],
          context.previousNotifications
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", userId] });
    },
  });

  const markAllAsRead = useMutation({
    mutationFn: async () => {
      const batch = writeBatch(db);
      notifications
        .filter((n) => !n.read)
        .forEach((n) => {
          const ref = doc(db, "notifications", n.id!);
          batch.update(ref, { read: true });
        });
      await batch.commit();
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["notifications", userId] });

      const previousNotifications = queryClient.getQueryData([
        "notifications",
        userId,
      ]);

      queryClient.setQueryData(
        ["notifications", userId],
        (old: INotificationV2[] = []) =>
          old.map((notification) => ({ ...notification, read: true }))
      );

      return { previousNotifications };
    },
    onError: (err, variables, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          ["notifications", userId],
          context.previousNotifications
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", userId] });
    },
  });

  const updateNotificationPreferences = useMutation({
    mutationFn: async (preferences: NotificationPreferences) => {
      if (!userId) {
        throw new Error(
          "User ID is required to update notification preferences"
        );
      }

      const result = await apiClient.shared.updateNotificationPreferences(
        userId,
        preferences
      );

      return result;
    },
    onMutate: async (newPreferences: NotificationPreferences) => {
      await queryClient.cancelQueries({
        queryKey: ["notificationPreferences", userId],
      });

      const previousPreferences = queryClient.getQueryData([
        "notificationPreferences",
        userId,
      ]);

      queryClient.setQueryData(
        ["notificationPreferences", userId],
        newPreferences
      );

      return { previousPreferences };
    },
    onError: (err, newPreferences, context) => {
      console.error("Error updating preferences:", err);

      if (context?.previousPreferences) {
        queryClient.setQueryData(
          ["notificationPreferences", userId],
          context.previousPreferences
        );
      }
    },
    onSuccess: (data) => {
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["notificationPreferences", userId],
      });
    },
  });

  return {
    notifications,
    notificationPreferences,
    refetchNotificationPreferences,
    markAsRead,
    markAllAsRead,
    updateNotificationPreferences,
  };
};
